import { Box, Link, Typography, useTheme } from "@mui/material";
import { EnBWScreenTemplate } from "../EnBWScreenTemplate";
import { useTranslation } from "react-i18next";
import { getUser, logout, refreshToken } from "@newsenec/auth-provider";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { screenRoutes } from "../../screenRoutes";
import { sendVerificationEmail } from "./emailVerification";
import { CheckmarkIcon, ChevronRightIcon, LogoutIcon, SenecButton, SenecFPPTheme } from "@newsenec/ui-components";

export const EmailVerificationScreen = () => {
    const [userEmail, setUserEmail] = useState<string | undefined>();
    const [isMailSent, setIsMailSent] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme: SenecFPPTheme = useTheme();

    useEffect(() => {
        getUser()
            .then((user) => {
                if (user?.email_verified) {
                    navigate(screenRoutes.registration);
                }
                setUserEmail(user?.email);
            })
            .catch(() => {
                //do nothing. step should never be reached
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onRefresh = () => {
        refreshToken().then(() => {
            window.location.reload();
        });
    };

    const onSendVerificationMail = async () => {
        setIsMailSent(true);
        await sendVerificationEmail();
    };

    return (
        <EnBWScreenTemplate>
            <Typography variant="h3">{t("emailVerificationScreen.title")}</Typography>
            <Typography>{t("emailVerificationScreen.description")}</Typography>
            <Typography data-testid="address-confirmation-prompt">
                {t("emailVerificationScreen.addressConfirmationPrompt1")}
                <b>{userEmail}</b>
                {t("emailVerificationScreen.addressConfirmationPrompt2")}
                <b>{t("emailVerificationScreen.addressConfirmationPrompt3")}</b>
            </Typography>

            <SenecButton onClick={onRefresh} data-testid="refresh-btn">
                {t("emailVerificationScreen.refreshButton")}
            </SenecButton>

            <Box
                display="flex"
                alignItems="center"
                sx={{
                    alignItems: { md: "center" },
                    flexDirection: { xs: "column", md: "row" },
                    gap: { xs: "0.5rem", md: "1rem" },
                }}
            >
                {isMailSent ? (
                    <>
                        <Typography variant="subtitle2" color={theme.palette.neutralGray70.main}>
                            {t("emailVerificationScreen.resendEmailConfirmationPrompt")}
                        </Typography>
                        <CheckmarkIcon sx={{ color: "grey" }} />
                    </>
                ) : (
                    <>
                        <Typography variant="subtitle2" color={theme.palette.neutralGray60.main}>
                            {t("emailVerificationScreen.noEmailReceivedPrompt")}
                        </Typography>
                        <Link
                            component="button"
                            onClick={onSendVerificationMail}
                            sx={{
                                textDecoration: "none",
                                display: "flex",
                                alignItems: "center",
                            }}
                            data-testid="resend-btn"
                        >
                            <Typography variant="h5">{t("emailVerificationScreen.resendEmailLink")}</Typography>
                            <ChevronRightIcon />
                        </Link>
                    </>
                )}
            </Box>

            <Box
                display="flex"
                alignItems="center"
                sx={{
                    alignItems: { md: "center" },
                    flexDirection: { xs: "column", md: "row" },
                    gap: { xs: "0.5rem", md: "1rem" },
                    marginTop: "0.5rem !important",
                }}
            >
                <Typography variant="subtitle2" color={theme.palette.neutralGray60.main}>
                    {t("emailVerificationScreen.changeMEKAccountPrompt")}
                </Typography>
                <Link
                    component="button"
                    onClick={async () => logout()}
                    sx={{
                        textDecoration: "none",
                        display: "flex",
                        alignItems: "center",
                    }}
                    data-testid="logout-btn"
                >
                    <Typography variant="h5">{t("emailVerificationScreen.changeMEKAccountLink")}</Typography>
                    <LogoutIcon sx={{ marginLeft: "0.5rem" }} />
                </Link>
            </Box>
        </EnBWScreenTemplate>
    );
};
